/**
 * ClassifierAdmin — Panel amigable del clasificador.
 * Diseño centrado en conversaciones reales, con lenguaje humano.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, Chip, IconButton, Tooltip,
  CircularProgress, Paper, Dialog, DialogTitle, DialogContent, DialogActions,
  LinearProgress, InputAdornment,
} from '@mui/material';
import {
  Home as HomeIcon, Forum as ChatIcon, School as TeachIcon,
  Science as TestIcon, Send as SendIcon, Check as CheckIcon,
  Close as CloseIcon, Delete as DeleteIcon, Add as AddIcon,
  Edit as EditIcon, Refresh as RefreshIcon, Search as SearchIcon,
  ThumbUp as ThumbUpIcon, ThumbDown as ThumbDownIcon,
  Insights as InsightsIcon, PlayArrow as PlayIcon, Info as InfoIcon,
  AutoFixHigh as AutoFixIcon, Person as PersonIcon,
  SmartToy as BotIcon, ArrowForward as ArrowIcon, Save as SaveIcon,
} from '@mui/icons-material';
import { auth } from '../firebase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Categorías en lenguaje humano
const CATEGORIAS = [
  { value: 'SALUDO', label: 'Saluda al chatbot', short: 'Saludo',
    descripcion: 'El paciente escribe "Hola", "Buenos días" o similar',
    color: '#10b981', icon: '👋' },
  { value: 'TOOL_USE', label: 'Quiere agendar o cancelar una cita', short: 'Cita',
    descripcion: 'El paciente quiere acciones sobre su cita',
    color: '#0ea5e9', icon: '📅' },
  { value: 'SINTOMAS', label: 'Reporta síntomas o malestar', short: 'Síntomas',
    descripcion: 'El paciente describe algo que siente físicamente',
    color: '#ef4444', icon: '🤒' },
  { value: 'MEMORIA', label: 'Pregunta sobre conversación anterior', short: 'Historial',
    descripcion: 'El paciente quiere recordar algo del pasado',
    color: '#8b5cf6', icon: '💭' },
  { value: 'INFORMACION', label: 'Pregunta sobre precios, horarios o servicios', short: 'Info',
    descripcion: 'El paciente quiere información del negocio',
    color: '#f59e0b', icon: '💬' },
  { value: 'CONVERSACION', label: 'Conversación general', short: 'General',
    descripcion: 'Confirmaciones, despedidas o charla casual',
    color: '#64748b', icon: '💭' },
  { value: 'ESCALACION', label: 'Quiere hablar con una persona real', short: 'Humano',
    descripcion: 'El paciente pide ser atendido por un humano',
    color: '#dc2626', icon: '🙋' },
];

const getCat = (value) => CATEGORIAS.find(c => c.value === value) || { label: value, color: '#64748b', short: value, icon: '❓' };

// Qué hace NARA después de clasificar (handler + tools)
const ACCIONES_POR_CATEGORIA = {
  SALUDO: {
    handler: 'handler_saludo',
    pasos: [
      { icon: '🤖', texto: 'Genera un saludo personalizado con GPT-4o-mini' },
      { icon: '👤', texto: 'Usa el nombre del paciente si está registrado' },
      { icon: '✉️', texto: 'Envía respuesta por WhatsApp' },
    ],
  },
  TOOL_USE: {
    handler: 'agent_tools',
    pasos: [
      { icon: '🔧', texto: 'Activa una de las 6 herramientas disponibles' },
      { icon: '📅', texto: 'Consultar disponibilidad · Crear cita · Cancelar · Reagendar · Buscar citas · Buscar info' },
      { icon: '💬', texto: 'GPT-4o ejecuta la acción con los datos del paciente' },
    ],
  },
  SINTOMAS: {
    handler: 'handler_sintomas',
    pasos: [
      { icon: '🩺', texto: 'Analiza todos los síntomas reportados como cuadro integral' },
      { icon: '📋', texto: 'Genera diagnóstico preliminar ordenado de benigno a serio' },
      { icon: '💚', texto: 'Responde con tono humanizado y emojis de cercanía' },
      { icon: '💾', texto: 'Guarda en memoria semántica (365 días)' },
    ],
  },
  MEMORIA: {
    handler: 'handler_memoria',
    pasos: [
      { icon: '🔍', texto: 'Busca conversaciones anteriores por similitud semántica' },
      { icon: '🧠', texto: 'GPT-4o-mini responde usando memorias encontradas' },
      { icon: '💚', texto: 'Tono tranquilizador para preguntas alarmantes' },
    ],
  },
  INFORMACION: {
    handler: 'handler_informacion',
    pasos: [
      { icon: '📚', texto: 'Busca en la Base de Conocimiento del chatbot' },
      { icon: '🤖', texto: 'GPT-4o-mini responde con la info encontrada' },
      { icon: '📝', texto: 'Usa datos reales (precios, horarios, ubicación)' },
    ],
  },
  CONVERSACION: {
    handler: 'handler_conversacion',
    pasos: [
      { icon: '💬', texto: 'Responde al intent del mensaje actual' },
      { icon: '📚', texto: 'Puede consultar KB si es relevante' },
      { icon: '👋', texto: 'Maneja despedidas y charla casual' },
    ],
  },
  ESCALACION: {
    handler: 'escalacion_handler',
    pasos: [
      { icon: '📞', texto: 'Notifica al doctor por WhatsApp' },
      { icon: '⏸️', texto: 'Marca sesión como "pendiente de humano"' },
      { icon: '💬', texto: 'NARA deja de responder hasta que el doctor intervenga' },
    ],
  },
};

export default function ClassifierAdmin() {
  const [userId, setUserId] = useState(null);
  const [seccion, setSeccion] = useState('inicio');

  // Data
  const [log, setLog] = useState([]);
  const [stats, setStats] = useState(null);
  const [ejemplos, setEjemplos] = useState([]);
  const [correcciones, setCorrecciones] = useState([]);
  const [prompts, setPrompts] = useState([]);

  // Dialog / forms
  const [corrigiendoItem, setCorrigiendoItem] = useState(null);
  const [agregarEjemplo, setAgregarEjemplo] = useState(false);
  const [nuevoEjemplo, setNuevoEjemplo] = useState({ example_message: '', expected_category: 'SALUDO', explanation: '' });
  const [editandoCategoria, setEditandoCategoria] = useState(null);
  const [contenidoEdit, setContenidoEdit] = useState('');

  // Test sandbox
  const [testMensaje, setTestMensaje] = useState('');
  const [testResultado, setTestResultado] = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [chatTest, setChatTest] = useState([]);

  // Auth
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      if (u) {
        const stored = localStorage.getItem('user_id');
        if (stored) { setUserId(stored); } else {
          setUserId(u.uid);
          try {
            const res = await fetch(`${API_URL}/users/by_uid/${u.uid}`);
            const data = await res.json();
            if (data?.id) { localStorage.setItem('user_id', String(data.id)); setUserId(String(data.id)); }
          } catch (e) { }
        }
      }
    });
    return () => unsub();
  }, []);

  const headers = { 'X-User-Id': userId || '', 'Content-Type': 'application/json' };

  const cargarDatos = useCallback(async () => {
    if (!userId) return;
    try {
      const [rLog, rStats, rEj, rCorr, rPrompts] = await Promise.all([
        fetch(`${API_URL}/admin/clasificador/log?limit=50`, { headers: { 'X-User-Id': userId } }),
        fetch(`${API_URL}/admin/clasificador/stats`, { headers: { 'X-User-Id': userId } }),
        fetch(`${API_URL}/admin/clasificador/ejemplos`, { headers: { 'X-User-Id': userId } }),
        fetch(`${API_URL}/admin/clasificador/correcciones`, { headers: { 'X-User-Id': userId } }),
        fetch(`${API_URL}/admin/clasificador/prompts`, { headers: { 'X-User-Id': userId } }),
      ]);
      if (rLog.ok) { const d = await rLog.json(); setLog(Array.isArray(d) ? d : (d.log || [])); }
      if (rStats.ok) setStats(await rStats.json());
      if (rEj.ok) { const d = await rEj.json(); setEjemplos(Array.isArray(d) ? d : (d.ejemplos || [])); }
      if (rCorr.ok) { const d = await rCorr.json(); setCorrecciones(Array.isArray(d) ? d : (d.correcciones || [])); }
      if (rPrompts.ok) { const d = await rPrompts.json(); setPrompts(Array.isArray(d) ? d : []); }
    } catch (e) { console.error(e); }
  }, [userId]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  const confirmar = async (id) => {
    await fetch(`${API_URL}/admin/clasificador/log/${id}/confirmar`, { method: 'POST', headers: { 'X-User-Id': userId } });
    cargarDatos();
  };

  const corregir = async (id, nuevaCat) => {
    await fetch(`${API_URL}/admin/clasificador/log/${id}/corregir`, {
      method: 'POST', headers, body: JSON.stringify({ categoria_correcta: nuevaCat }),
    });
    setCorrigiendoItem(null);
    cargarDatos();
  };

  const guardarEjemplo = async () => {
    if (!nuevoEjemplo.example_message) return;
    await fetch(`${API_URL}/admin/clasificador/ejemplos`, { method: 'POST', headers, body: JSON.stringify(nuevoEjemplo) });
    setNuevoEjemplo({ example_message: '', expected_category: 'SALUDO', explanation: '' });
    setAgregarEjemplo(false);
    cargarDatos();
  };

  const eliminarEjemplo = async (id) => {
    await fetch(`${API_URL}/admin/clasificador/ejemplos/${id}`, { method: 'DELETE', headers: { 'X-User-Id': userId } });
    cargarDatos();
  };

  const guardarComportamiento = async (categoriaValue) => {
    const clave = `handler_${categoriaValue.toLowerCase()}`;
    await fetch(`${API_URL}/admin/clasificador/prompts/${clave}`, {
      method: 'PUT', headers, body: JSON.stringify({ contenido: contenidoEdit }),
    });
    setEditandoCategoria(null);
    cargarDatos();
  };

  const restaurarComportamiento = async (categoriaValue) => {
    if (!window.confirm('¿Restaurar la forma de responder original para esta situación?')) return;
    const clave = `handler_${categoriaValue.toLowerCase()}`;
    await fetch(`${API_URL}/admin/clasificador/prompts/${clave}/restaurar`, { method: 'POST', headers: { 'X-User-Id': userId } });
    setEditandoCategoria(null);
    cargarDatos();
  };

  const probar = async (msg) => {
    const mensaje = msg || testMensaje;
    if (!mensaje) return;
    setChatTest(prev => [...prev, { role: 'user', text: mensaje }]);
    setTestMensaje('');
    setTestLoading(true);
    try {
      const res = await fetch(`${API_URL}/admin/clasificador/test`, { method: 'POST', headers, body: JSON.stringify({ mensaje, chatbot_id: 63 }) });
      if (res.ok) {
        const data = await res.json();
        const cat = data.resultado?.action || data.action;
        const reasoning = data.resultado?.reasoning || data.reasoning;
        setChatTest(prev => [...prev, { role: 'bot', categoria: cat, reasoning, confidence: data.resultado?.confidence || data.confidence, details: data.resultado?.details || data.details }]);
        setTestResultado(data.resultado || data);
      }
    } catch (e) { console.error(e); }
    setTestLoading(false);
  };

  // Conversaciones pendientes de revisar
  const pendientes = log.filter(l => l.fue_correcta === null);
  const correctas = log.filter(l => l.fue_correcta === true).length;
  const incorrectas = log.filter(l => l.fue_correcta === false).length;
  const precision = (correctas + incorrectas) > 0 ? Math.round(correctas / (correctas + incorrectas) * 100) : null;

  const NAV = [
    { key: 'inicio', label: 'Inicio', icon: <HomeIcon /> },
    { key: 'conversaciones', label: 'Conversaciones', icon: <ChatIcon />, badge: pendientes.length },
    { key: 'enseñar', label: 'Enseñar a NARA', icon: <TeachIcon /> },
    { key: 'probar', label: 'Probar', icon: <TestIcon /> },
  ];

  return (
    <Box sx={{ height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', background: '#f8fafc' }}>
      {/* Header */}
      <Box sx={{
        px: 3, py: 2, background: '#fff', borderBottom: '1px solid #e2e8f0',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Cómo entiende NARA a tus pacientes</Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>Revisa, enseña y mejora cómo responde el chatbot</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {NAV.map(n => {
            const activa = seccion === n.key;
            return (
              <Button key={n.key} onClick={() => setSeccion(n.key)} startIcon={n.icon}
                sx={{
                  textTransform: 'none', fontWeight: activa ? 700 : 500, fontSize: 13,
                  color: activa ? '#6366f1' : '#64748b', borderRadius: 2, px: 2,
                  bgcolor: activa ? '#eef2ff' : 'transparent',
                  '&:hover': { bgcolor: activa ? '#e0e7ff' : '#f1f5f9' },
                }}>
                {n.label}
                {n.badge > 0 && (
                  <Chip label={n.badge} size="small"
                    sx={{ ml: 0.8, height: 18, fontSize: 10, fontWeight: 700, bgcolor: '#ef4444', color: '#fff' }} />
                )}
              </Button>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>

        {/* ════════════════ INICIO ════════════════ */}
        {seccion === 'inicio' && (
          <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#0f172a', mb: 0.5 }}>¿Cómo te está yendo hoy?</Typography>
            <Typography sx={{ fontSize: 14, color: '#64748b', mb: 3 }}>
              Este es el resumen de cómo está trabajando NARA con tus pacientes
            </Typography>

            {/* KPIs grandes */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 4 }}>
              {[
                { label: 'Conversaciones revisadas', value: stats?.total || 0, sub: 'Clasificadas por NARA', color: '#6366f1', icon: <ChatIcon /> },
                { label: 'Aciertos', value: precision !== null ? `${precision}%` : '—', sub: precision !== null ? `${correctas} de ${correctas + incorrectas}` : 'Sin datos aún', color: '#10b981', icon: <ThumbUpIcon /> },
                { label: 'Correcciones aplicadas', value: correcciones.length, sub: 'NARA aprendió de estos', color: '#f59e0b', icon: <AutoFixIcon /> },
                { label: 'Sin revisar', value: pendientes.length, sub: 'Necesitan tu atención', color: '#ef4444', icon: <InsightsIcon /> },
              ].map(kpi => (
                <Paper key={kpi.label} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: `${kpi.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {React.cloneElement(kpi.icon, { sx: { fontSize: 20, color: kpi.color } })}
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: 32, fontWeight: 800, color: '#0f172a', lineHeight: 1 }}>{kpi.value}</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a', mt: 0.5 }}>{kpi.label}</Typography>
                  <Typography sx={{ fontSize: 11, color: '#94a3b8', mt: 0.3 }}>{kpi.sub}</Typography>
                </Paper>
              ))}
            </Box>

            {/* Conversaciones que necesitan atención */}
            {pendientes.length > 0 ? (
              <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #fde68a', bgcolor: '#fffbeb', boxShadow: 'none', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#92400e' }}>
                      {pendientes.length} {pendientes.length === 1 ? 'conversación necesita' : 'conversaciones necesitan'} tu atención
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#b45309' }}>
                      Revisa si NARA entendió bien — tu feedback la hace mejor
                    </Typography>
                  </Box>
                  <Button onClick={() => setSeccion('conversaciones')} endIcon={<ArrowIcon />}
                    sx={{ textTransform: 'none', fontWeight: 600, color: '#92400e' }}>
                    Ver todas
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {pendientes.slice(0, 3).map(item => {
                    const cat = getCat(item.categoria_asignada);
                    return (
                      <ConversacionCard key={item.id} item={item} cat={cat}
                        onConfirm={() => confirmar(item.id)}
                        onCorrect={() => setCorrigiendoItem(item)}
                      />
                    );
                  })}
                </Box>
              </Paper>
            ) : (
              <Paper sx={{ p: 4, borderRadius: 3, border: '1px solid #d1fae5', bgcolor: '#f0fdf4', boxShadow: 'none', textAlign: 'center', mb: 3 }}>
                <CheckIcon sx={{ fontSize: 48, color: '#10b981', mb: 1 }} />
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#065f46' }}>Todo al día</Typography>
                <Typography sx={{ fontSize: 12, color: '#047857' }}>No hay conversaciones pendientes de revisar</Typography>
              </Paper>
            )}

            {/* Acciones rápidas */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
              {[
                { title: 'Enseñar con ejemplos', desc: 'Agrega frases que NARA debería reconocer', icon: <TeachIcon />, color: '#8b5cf6', action: () => setSeccion('enseñar') },
                { title: 'Probar mensajes', desc: 'Ver cómo respondería NARA antes de producción', icon: <TestIcon />, color: '#0ea5e9', action: () => setSeccion('probar') },
                { title: 'Ver conversaciones', desc: 'Historial completo de cómo clasificó', icon: <ChatIcon />, color: '#10b981', action: () => setSeccion('conversaciones') },
              ].map(a => (
                <Paper key={a.title} onClick={a.action} sx={{
                  p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none', cursor: 'pointer',
                  transition: 'all 0.2s', '&:hover': { borderColor: a.color, transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' },
                }}>
                  <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: `${a.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                    {React.cloneElement(a.icon, { sx: { fontSize: 22, color: a.color } })}
                  </Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0f172a', mb: 0.3 }}>{a.title}</Typography>
                  <Typography sx={{ fontSize: 12, color: '#64748b' }}>{a.desc}</Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        )}

        {/* ════════════════ CONVERSACIONES ════════════════ */}
        {seccion === 'conversaciones' && (
          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>Conversaciones recientes</Typography>
                <Typography sx={{ fontSize: 12, color: '#64748b' }}>Cada fila es un mensaje que NARA analizó</Typography>
              </Box>
              <IconButton onClick={cargarDatos} sx={{ color: '#64748b' }}><RefreshIcon /></IconButton>
            </Box>

            {log.length === 0 ? (
              <Paper sx={{ p: 6, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none', textAlign: 'center' }}>
                <ChatIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#64748b' }}>Todavía no hay conversaciones</Typography>
                <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>Cuando los pacientes escriban al chatbot, aparecerán aquí</Typography>
              </Paper>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {log.map(item => {
                  const cat = getCat(item.categoria_asignada);
                  return (
                    <ConversacionCard key={item.id} item={item} cat={cat}
                      onConfirm={() => confirmar(item.id)}
                      onCorrect={() => setCorrigiendoItem(item)}
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        {/* ════════════════ ENSEÑAR A NARA ════════════════ */}
        {seccion === 'enseñar' && (
          <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0f172a', mb: 0.5 }}>Enseña a NARA cómo actuar</Typography>
            <Typography sx={{ fontSize: 13, color: '#64748b', mb: 3 }}>
              Personaliza cómo responde cuando un paciente escribe cada tipo de mensaje
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {CATEGORIAS.map(cat => {
                const handlerPrompt = prompts.find(p => p.clave === `handler_${cat.value.toLowerCase()}`);
                const ejemplosDeCat = ejemplos.filter(e => e.expected_category === cat.value);
                const estaEditando = editandoCategoria === cat.value;

                return (
                  <Paper key={cat.value} sx={{ p: 0, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none', overflow: 'hidden' }}>
                    <Box sx={{ px: 2.5, py: 1.8, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{ fontSize: 22 }}>{cat.icon}</Box>
                        <Box>
                          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                            Cuando el paciente {cat.label.toLowerCase()}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{cat.descripcion}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {ejemplosDeCat.length > 0 && (
                          <Chip label={`${ejemplosDeCat.length} ${ejemplosDeCat.length === 1 ? 'ejemplo' : 'ejemplos'}`} size="small"
                            sx={{ height: 22, fontSize: 10, bgcolor: '#f1f5f9', color: '#64748b' }} />
                        )}
                      </Box>
                    </Box>

                    <Box sx={{ p: 2.5, bgcolor: '#fafbfc' }}>
                      {/* Cómo debe responder */}
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
                          Cómo debe responder NARA
                        </Typography>
                        {handlerPrompt ? (
                          estaEditando ? (
                            <Box>
                              <TextField fullWidth multiline minRows={4} maxRows={12}
                                value={contenidoEdit} onChange={(e) => setContenidoEdit(e.target.value)}
                                placeholder="Describe cómo quieres que NARA responda..."
                                sx={{ mb: 1, '& .MuiOutlinedInput-root': { background: '#fff', fontSize: 13, lineHeight: 1.7, borderRadius: 2 } }}
                              />
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button size="small" onClick={() => restaurarComportamiento(cat.value)}
                                  sx={{ textTransform: 'none', fontSize: 11, color: '#f59e0b' }}>
                                  Restaurar original
                                </Button>
                                <Button size="small" onClick={() => { setEditandoCategoria(null); setContenidoEdit(''); }}
                                  sx={{ textTransform: 'none', fontSize: 11, color: '#64748b' }}>
                                  Cancelar
                                </Button>
                                <Button size="small" variant="contained" startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
                                  onClick={() => guardarComportamiento(cat.value)}
                                  sx={{ textTransform: 'none', fontSize: 11, fontWeight: 600, background: cat.color, '&:hover': { background: cat.color, opacity: 0.9 } }}>
                                  Guardar
                                </Button>
                              </Box>
                            </Box>
                          ) : (
                            <Box onClick={() => { setEditandoCategoria(cat.value); setContenidoEdit(handlerPrompt.contenido || ''); }}
                              sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fff', border: '1px solid #e2e8f0', cursor: 'pointer',
                                '&:hover': { borderColor: cat.color, bgcolor: `${cat.color}05` } }}>
                              <Typography sx={{ fontSize: 12, color: '#475569', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                                {handlerPrompt.contenido?.substring(0, 300)}
                                {(handlerPrompt.contenido?.length || 0) > 300 && '...'}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, color: cat.color }}>
                                <EditIcon sx={{ fontSize: 14 }} />
                                <Typography sx={{ fontSize: 11, fontWeight: 600 }}>Editar</Typography>
                              </Box>
                            </Box>
                          )
                        ) : (
                          <Paper sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px dashed #cbd5e1', boxShadow: 'none' }}>
                            <Typography sx={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
                              Esta categoría aún no tiene comportamiento personalizable desde aquí
                            </Typography>
                          </Paper>
                        )}
                      </Box>

                      {/* Ejemplos */}
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Frases que la activan
                          </Typography>
                          <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                            onClick={() => { setNuevoEjemplo({ example_message: '', expected_category: cat.value, explanation: '' }); setAgregarEjemplo(true); }}
                            sx={{ textTransform: 'none', fontSize: 10, fontWeight: 600, color: cat.color }}>
                            Agregar ejemplo
                          </Button>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {ejemplosDeCat.length === 0 ? (
                            <Typography sx={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
                              Sin ejemplos. Agrega frases como "me duele la cabeza" o "cuánto cuesta una consulta"
                            </Typography>
                          ) : (
                            ejemplosDeCat.map(ej => (
                              <Chip key={ej.id} label={`"${ej.example_message}"`} size="small"
                                onDelete={() => eliminarEjemplo(ej.id)}
                                sx={{ fontSize: 11, bgcolor: `${cat.color}15`, color: cat.color, border: `1px solid ${cat.color}30` }}
                              />
                            ))
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          </Box>
        )}

        {/* ════════════════ PROBAR ════════════════ */}
        {seccion === 'probar' && (
          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0f172a', mb: 0.5 }}>Probar mensajes</Typography>
            <Typography sx={{ fontSize: 13, color: '#64748b', mb: 3 }}>
              Escribe lo que un paciente podría decir y mira cómo lo interpretaría NARA
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              {/* Chat mockup */}
              <Paper sx={{ borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', overflow: 'hidden', bgcolor: '#fff' }}>
                <Box sx={{ px: 2, py: 1.5, bgcolor: 'linear-gradient(135deg, #6366f1, #8b5cf6)', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <BotIcon sx={{ fontSize: 18, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>NARA</Typography>
                    <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.8)' }}>Modo prueba</Typography>
                  </Box>
                </Box>

                <Box sx={{ minHeight: 320, maxHeight: 400, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {chatTest.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <ChatIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                      <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>Escribe un mensaje abajo para comenzar</Typography>
                    </Box>
                  )}
                  {chatTest.map((msg, i) => (
                    msg.role === 'user' ? (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Box sx={{ maxWidth: '80%', px: 2, py: 1, borderRadius: 2.5, bgcolor: '#6366f1', color: '#fff' }}>
                          <Typography sx={{ fontSize: 13 }}>{msg.text}</Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Box key={i} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                        <Box sx={{ maxWidth: '95%', p: 1.5, borderRadius: 2.5, bgcolor: '#f1f5f9', border: '1px solid #e2e8f0' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Box sx={{ fontSize: 16 }}>{getCat(msg.categoria).icon}</Box>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: getCat(msg.categoria).color }}>
                              {getCat(msg.categoria).label}
                            </Typography>
                            <Chip label={`${Math.round((msg.confidence || 0) * 100)}%`} size="small"
                              sx={{ height: 16, fontSize: 9, ml: 'auto', bgcolor: '#e2e8f0' }} />
                          </Box>
                          <Typography sx={{ fontSize: 11, color: '#64748b', lineHeight: 1.5, mb: 1 }}>
                            {msg.reasoning}
                          </Typography>
                          {/* Pasos de ejecución */}
                          {ACCIONES_POR_CATEGORIA[msg.categoria] && (
                            <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #cbd5e1' }}>
                              <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.6 }}>
                                Qué hace NARA a continuación
                              </Typography>
                              {ACCIONES_POR_CATEGORIA[msg.categoria].pasos.map((p, pi) => (
                                <Box key={pi} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.3 }}>
                                  <Box sx={{ fontSize: 11, width: 16 }}>{p.icon}</Box>
                                  <Typography sx={{ fontSize: 10, color: '#475569', lineHeight: 1.4 }}>{p.texto}</Typography>
                                </Box>
                              ))}
                              {msg.details?.tool && (
                                <Box sx={{ mt: 0.8, px: 1, py: 0.5, borderRadius: 1, bgcolor: `${getCat(msg.categoria).color}15`, display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                                  <Typography sx={{ fontSize: 9, color: '#94a3b8', fontWeight: 600 }}>Herramienta:</Typography>
                                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: getCat(msg.categoria).color, fontFamily: 'monospace' }}>
                                    {msg.details.tool}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )
                  ))}
                  {testLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <Box sx={{ px: 2, py: 1, borderRadius: 2.5, bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={12} sx={{ color: '#6366f1' }} />
                        <Typography sx={{ fontSize: 11, color: '#64748b' }}>Analizando...</Typography>
                      </Box>
                    </Box>
                  )}
                </Box>

                <Box sx={{ p: 1.5, borderTop: '1px solid #e2e8f0', display: 'flex', gap: 1 }}>
                  <TextField fullWidth size="small" placeholder="Escribe un mensaje de prueba..."
                    value={testMensaje} onChange={(e) => setTestMensaje(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !testLoading) probar(); }}
                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f8fafc', fontSize: 13, borderRadius: 3 } }}
                  />
                  <IconButton onClick={() => probar()} disabled={!testMensaje || testLoading}
                    sx={{ bgcolor: '#6366f1', color: '#fff', '&:hover': { bgcolor: '#4f46e5' }, '&:disabled': { bgcolor: '#e2e8f0' } }}>
                    <SendIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Paper>

              {/* Ejemplos rápidos */}
              <Box>
                <Paper sx={{ p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none', mb: 2 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', mb: 1.5 }}>Prueba rápida</Typography>
                  <Typography sx={{ fontSize: 11, color: '#64748b', mb: 1.5 }}>
                    Haz clic en un mensaje para probarlo
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {[
                      'Hola buenos días',
                      'Quiero agendar una cita',
                      'Me duele la cabeza y tengo fiebre',
                      '¿Cuánto cuesta la consulta?',
                      '¿Qué me habías recomendado?',
                      'Quiero hablar con una persona',
                      'Gracias, adiós',
                    ].map(msg => (
                      <Button key={msg} onClick={() => probar(msg)} disabled={testLoading}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', fontSize: 12, color: '#64748b',
                          bgcolor: '#f8fafc', borderRadius: 2, px: 1.5, py: 0.8,
                          '&:hover': { bgcolor: '#f1f5f9', color: '#6366f1' } }}>
                        "{msg}"
                      </Button>
                    ))}
                  </Box>
                </Paper>
                {testResultado && (
                  <Paper sx={{ p: 2, borderRadius: 3, border: `2px solid ${getCat(testResultado.action).color}`, boxShadow: 'none' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
                      Último análisis
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Box sx={{ fontSize: 24 }}>{getCat(testResultado.action).icon}</Box>
                      <Box>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: getCat(testResultado.action).color }}>
                          {getCat(testResultado.action).label}
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>
                          Certeza: {Math.round((testResultado.confidence || 0) * 100)}%
                        </Typography>
                      </Box>
                    </Box>
                    {testResultado.details?.tool && (
                      <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: `${getCat(testResultado.action).color}10`, mb: 1 }}>
                        <Typography sx={{ fontSize: 9, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Herramienta activada</Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: getCat(testResultado.action).color, fontFamily: 'monospace', mt: 0.3 }}>
                          {testResultado.details.tool}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ pt: 1, borderTop: '1px dashed #e2e8f0' }}>
                      <Typography sx={{ fontSize: 9, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Ruta de ejecución</Typography>
                      {(ACCIONES_POR_CATEGORIA[testResultado.action]?.pasos || []).map((p, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.3 }}>
                          <Box sx={{ fontSize: 11 }}>{p.icon}</Box>
                          <Typography sx={{ fontSize: 10, color: '#475569', lineHeight: 1.4 }}>{p.texto}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Diálogo de corrección */}
      <Dialog open={!!corrigiendoItem} onClose={() => setCorrigiendoItem(null)} maxWidth="sm" fullWidth>
        {corrigiendoItem && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Corrigiendo NARA</Typography>
              <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.3 }}>
                Elige lo que NARA debería haber entendido
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              <Paper sx={{ p: 2, mb: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Typography sx={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>El paciente escribió</Typography>
                <Typography sx={{ fontSize: 14, color: '#0f172a' }}>"{corrigiendoItem.mensaje}"</Typography>
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>NARA entendió:</Typography>
                  <Chip label={getCat(corrigiendoItem.categoria_asignada).label} size="small"
                    sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: getCat(corrigiendoItem.categoria_asignada).color, color: '#fff' }} />
                </Box>
              </Paper>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#0f172a', mb: 1 }}>
                ¿Qué debería haber entendido?
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {CATEGORIAS.filter(c => c.value !== corrigiendoItem.categoria_asignada).map(c => (
                  <Button key={c.value} onClick={() => corregir(corrigiendoItem.id, c.value)}
                    sx={{ justifyContent: 'flex-start', textTransform: 'none', p: 1.5, borderRadius: 2,
                      border: '1px solid #e2e8f0', bgcolor: '#fff',
                      '&:hover': { borderColor: c.color, bgcolor: `${c.color}08` } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                      <Box sx={{ fontSize: 20 }}>{c.icon}</Box>
                      <Box sx={{ textAlign: 'left', flex: 1 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{c.label}</Typography>
                        <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>{c.descripcion}</Typography>
                      </Box>
                    </Box>
                  </Button>
                ))}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCorrigiendoItem(null)} sx={{ textTransform: 'none', color: '#64748b' }}>Cancelar</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Diálogo agregar ejemplo */}
      <Dialog open={agregarEjemplo} onClose={() => setAgregarEjemplo(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>Agregar ejemplo</Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            Enseña a NARA que este tipo de mensaje significa: <strong>{getCat(nuevoEjemplo.expected_category).label}</strong>
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Frase del paciente" placeholder="Ej: Dame la cuenta para pagar"
            value={nuevoEjemplo.example_message}
            onChange={(e) => setNuevoEjemplo({ ...nuevoEjemplo, example_message: e.target.value })}
            InputLabelProps={{ shrink: true }} sx={{ mt: 2, mb: 2 }}
          />
          <TextField fullWidth label="Explicación (opcional)" placeholder="Por qué este mensaje debe clasificarse así"
            value={nuevoEjemplo.explanation}
            onChange={(e) => setNuevoEjemplo({ ...nuevoEjemplo, explanation: e.target.value })}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAgregarEjemplo(false)} sx={{ textTransform: 'none', color: '#64748b' }}>Cancelar</Button>
          <Button variant="contained" onClick={guardarEjemplo} disabled={!nuevoEjemplo.example_message}
            sx={{ textTransform: 'none', bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}>
            Agregar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Componente tarjeta de conversación ───
function ConversacionCard({ item, cat, onConfirm, onCorrect }) {
  const fecha = item.created_at ? new Date(item.created_at).toLocaleString('es', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
  const revisada = item.fue_correcta !== null;
  const fueIncorrecta = item.fue_correcta === false;

  return (
    <Paper sx={{
      p: 2, borderRadius: 3, border: '1px solid #e2e8f0', boxShadow: 'none',
      borderLeft: `4px solid ${fueIncorrecta ? '#ef4444' : revisada ? '#10b981' : cat.color}`,
      '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <PersonIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, color: '#0f172a', mb: 0.5 }}>"{item.mensaje}"</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>NARA entendió:</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
              <Box sx={{ fontSize: 14 }}>{cat.icon}</Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: cat.color }}>{cat.label}</Typography>
            </Box>
            {item.confianza !== null && item.confianza !== undefined && (
              <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>· {Math.round(item.confianza * 100)}% certeza</Typography>
            )}
            <Typography sx={{ fontSize: 10, color: '#cbd5e1', ml: 'auto' }}>{fecha}</Typography>
          </Box>
          {fueIncorrecta && item.categoria_corregida && (
            <Box sx={{ mt: 1, p: 1, borderRadius: 1.5, bgcolor: '#fef2f2', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography sx={{ fontSize: 10, color: '#991b1b' }}>Debería haber sido:</Typography>
              <Chip label={getCat(item.categoria_corregida).label} size="small"
                sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: getCat(item.categoria_corregida).color, color: '#fff' }} />
            </Box>
          )}
        </Box>
        {!revisada && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
            <Tooltip title="Está bien">
              <IconButton size="small" onClick={onConfirm}
                sx={{ color: '#94a3b8', '&:hover': { color: '#10b981', bgcolor: '#f0fdf4' } }}>
                <ThumbUpIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Corregir">
              <IconButton size="small" onClick={onCorrect}
                sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444', bgcolor: '#fef2f2' } }}>
                <ThumbDownIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Paper>
  );
}
